/* eslint-disable eqeqeq */
/**
 * PLATE HEAT EXCHANGER rated-output sheet — React port of
 * `plate_heat_exchanger.rated.outputs.tmpl` (1,246 lines) plus the parts of
 * `plate_heat_exchanger.selector.js` that drive it (`render_outputs` :2898-3093,
 * `render_phxs`/`showOrHideButtons`/`prev_next`/`changePhx` :3138-3199,
 * `checkOutputPerformanceTab`/`syncInputPerformanceTab`/`isSummerOrWinterTabActive`
 * :3094-3136, `activateLabel` :1746-1768).
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────
 * The migration ported every other selector's output pane as a dedicated
 * component (EnergyWheelRatedOutputs, GasHeaterRatedOutputs, uvcRatedOutputs)
 * but PHX was left on the generic `RatedOutputsTable` — a five-column candidate
 * table (Model | Wet Eff Wi % | Dry Eff Wi % | SA APD Wi | EA APD Wi) that does
 * not exist anywhere in the legacy app. dev renders a TABBED output pane with
 * its own `Output · Heat-Exchanger · Performance · Bypass · Notes` rail, a
 * numbered candidate pager, and label/value grids per tab. This restores that.
 *
 * ── TWO LEGACY CONTEXT KEYS THAT LOOK LOAD-BEARING AND ARE NOT ────────────
 *  1. `selectedModelData` — `render_outputs` builds it as
 *     `{shippingWeight: '', selectedModel: ''}` and, when the catalogue field
 *     has a pick, REPLACES it with `{selectedModel: rated_phx}` (js:2901-2904 +
 *     :2925-2929). It never gains any other key, so EVERY
 *     `{{selectedModelData.<field>}}` fallback in the template — casing_width,
 *     airway_width, dry_temp_ratio_winter, sa_db_temp_summer, … — resolves to
 *     undefined and prints empty. They are modelled here as no-ops (the
 *     `rated_phx` value alone), which is why the winter and summer performance
 *     columns come out symmetrical even though the template's two halves were
 *     edited at different times and disagree about which rows carry a fallback.
 *     The one visible consequence is that `{{selectedModelData.selectedModel}}`
 *     — the Exchanger Model fallback — holds an OBJECT on dev and renders
 *     "[object Object]". We render '' instead; see PR notes.
 *  2. `phxData` — the first row of a re-fetched, re-filtered master catalogue
 *     collection (js:2965-2990). The only fields read off it are
 *     `casing_depth` / `plate_spacing` / `plate_thickness`, all of which are
 *     the current selection's catalogue row, i.e. exactly the `master_phx` the
 *     component detail already carries. The caller passes that instead of
 *     firing a second catalogue fetch per rate.
 *
 * ── NUMBER FORMATTING IS PER-SPAN, NOT PER-COLUMN ─────────────────────────
 * `numberFormatting()` (js:1782-1792) only formats spans carrying
 * `data-formatter_type`; everything else prints the API value raw. So `dec()`
 * and `int()` appear ONLY on the rows whose template span has the attribute,
 * and `raw()` everywhere else. Note the Bypass "Air PD" span is misspelled
 * `data-formater_type` in the template (tmpl:392) — it is therefore NOT
 * formatted on dev, and is `raw()` here.
 */

import { useState } from 'react';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';

export type PhxRow = Record<string, unknown>;

export interface PhxOptionRow extends Record<string, unknown> {
  id?: number | string | null;
  name?: string | null;
  value?: number | string | null;
}

/** the four output tabs, in template order (tmpl:3-19). */
export type PhxOutputTab = 'properties' | 'performance' | 'bypass' | 'notes';
/** the Performance pane's season sub-tab — SHARED with the input pane. */
export type PhxSeason = 'winter' | 'summer';

export interface PlateHeatExchangerRatedOutputsProps {
  /** the rated candidate rows (legacy `rated_phxs`), in API order. */
  rows: PhxRow[];
  /** index into `rows` of the picked candidate (legacy `rated_phx_index`). */
  ratedIndex: number;
  /** pager chip click (legacy `changePhx`, js:3194-3199). */
  onSelectRow: (index: number) => void;
  /**
   * legacy `self.from_api` — `render_phxs` only fills the chip strip when the
   * rows came from a RATE (`if (self.from_api && self.rated_phxs)`, js:3033).
   * Reopening a dialog on a saved model renders the strip EMPTY, so the strip is
   * chip-less until you press RATE.
   */
  fromApi: boolean;
  /**
   * legacy `rated_phx` AFTER the fallback at js:3011 — `rated_phxs[idx]` when it
   * has keys, otherwise `current_model.toJSON()`.
   */
  ratedPhx: PhxRow;
  /** legacy `current_model.toJSON()` — the saved component row. */
  currentModel: PhxRow;
  /** legacy `state` = `self.stateData.toJSON()` (the live input-field mirror). */
  state: PhxRow;
  /** legacy `phxData` — see the header note; the component's master_phx row. */
  phxData: PhxRow;
  /** legacy `dataForRate` — the accumulated rate input. */
  dataForRate: PhxRow;
  /** legacy `bank_deep` (`self.bank_deep`). */
  bankDeep: unknown;
  /** legacy `airStreamCollection.toJSON()`. */
  airStreams: PhxOptionRow[];
  /** legacy `shaft_shape` / `shaft_diameter` / `shaft_length` collections. */
  shaftShapes: PhxOptionRow[];
  shaftDiameters: PhxOptionRow[];
  shaftLengths: PhxOptionRow[];
  /** legacy `notes` — ALREADY filtered to the current manufacturer (js:3007). */
  notes: Array<{ note?: unknown }>;
  /** legacy `error` — the rate response's `errors` array. */
  errors: string[];
  /** legacy `warnings` — lights the Notes anchor (js:3069-3071). */
  warnings: string[];
  /** legacy `splitedNotes` — the `key : value` warning map (js:2930-2960). */
  splitedNotes: Record<string, unknown> | null;
  /** legacy `notFound` (js:2962): rated, nothing came back, no errors. */
  notFound: boolean;
  /** legacy `rated_datetime`; falls back to `current_model.rating_datetime`. */
  ratedDatetime: string | null;
  /** legacy `version` — the vendor DLL release (non-Heatex only). */
  version: string | null;
  /** `mfg_id == 52` (js:3025). */
  isHeatex: boolean;
  /** the season sub-tab, SHARED with the input pane (see the note on it). */
  season: PhxSeason;
  onSeasonChange: (season: PhxSeason) => void;
  /** which output tab is open (legacy `activeOutputTabId`, js:3037-3050). */
  outputTab: PhxOutputTab;
  onOutputTabChange: (tab: PhxOutputTab) => void;
}

// ---------------------------------------------------------------------------
// value helpers (the handlebars helpers this template uses)
// ---------------------------------------------------------------------------

/** `data-formatter_type="decimal"` — thousands separators + 2 dp. */
const dec = (v: unknown): string => (v == null || v === '' ? '' : formatDecimalDisplay(v));
/** `data-formatter_type="integer"` — thousands separators, no decimals. */
const int = (v: unknown): string => (v == null || v === '' ? '' : formatIntegerDisplay(v));
/** no `data-formatter_type` — handlebars prints the value as-is; null → ''. */
const raw = (v: unknown): string => (v == null ? '' : String(v));
/** `get_one` (handlebars/helpers.js) — first truthy non-object arg, else ''. */
const one = (...vals: unknown[]): unknown => vals.find((v) => v && typeof v !== 'object') ?? '';
/** handlebars `{{#if}}` truthiness: null/undefined/''/false/0/[] are empty. */
const hbTruthy = (v: unknown): boolean =>
  !(v == null || v === '' || v === false || v === 0 || (Array.isArray(v) && !v.length));

/** `{{#if a}}{{a}}{{else}}{{b}}{{/if}}` — the template's ubiquitous fallback. */
const pick = (a: unknown, b: unknown): unknown => (hbTruthy(a) ? a : b);

/** resolve an option list entry by `id` and print its `name` (`{{#each}}` +
 *  `{{#ifCompare id '==' …}}` — the template's lookup idiom). */
const byId = (list: PhxOptionRow[], id: unknown): PhxOptionRow | undefined =>
  id == null || id === '' ? undefined : list.find((o) => o.id == id);
/** same, matched on `value` instead of `id` (the damper-shaft spans). */
const byValue = (list: PhxOptionRow[], value: unknown): PhxOptionRow | undefined =>
  value == null || value === '' ? undefined : list.find((o) => o.value == value);

// ---------------------------------------------------------------------------
// row primitives
// ---------------------------------------------------------------------------

/**
 * The Heat-Exchanger / Performance left-column row: a `col-xs-4` label with an
 * optional right-floated unit, and a `col-xs-8 output-part` holding the values.
 * The `.row > .col-xs-12.left-padding-5` wrapper inside `.output-part` is
 * legacy's and is what indents the value off the label rule.
 */
const LeftRow = ({
  label,
  unit,
  unitClass = 'right uom',
  dataName = 'bypass_width_id',
  rowClass,
  children,
}: {
  label: React.ReactNode;
  unit?: string;
  unitClass?: string;
  dataName?: string;
  rowClass?: string;
  children: React.ReactNode;
}) => (
  <div className={rowClass ? `row ${rowClass}` : 'row'}>
    <div className="col-xs-4 label-part">
      <span data-name={dataName}>{label}</span>
      {unit && <span className={unitClass}>{unit}</span>}
    </div>
    <div className="col-xs-8 output-part">
      <div className="row">{children}</div>
    </div>
  </div>
);

/** the single-value body of a LeftRow (tmpl's `.col-xs-12.left-padding-5`). */
const OneValue = ({ children }: { children: React.ReactNode }) => (
  <div className="col-xs-12 left-padding-5">{children}</div>
);

/**
 * The Performance pane's paired SUPPLY / EXHAUST value cells (tmpl:464-484).
 * Legacy's supply cell is `col-xs-5 output-part-left left-padding-5` and puts
 * `left-padding-5` on the DIV; the exhaust cell is `col-xs-6 col-xs-offset-1`
 * and puts `left-padding-5` on the SPAN instead. That asymmetry is real — it
 * is what lines the exhaust column up under its heading — so it is preserved.
 */
const PairValues = ({ supply, exhaust }: { supply: React.ReactNode; exhaust: React.ReactNode }) => (
  <>
    <div className="col-xs-5 output-part-left left-padding-5">{supply}</div>
    <div className="col-xs-6 col-xs-offset-1">{exhaust}</div>
  </>
);

/**
 * The Performance pane's RIGHT ("General") column row — `col-xs-8` label /
 * `col-xs-4` value, value cell `col-xs-12 output-part-left no-left-padding`.
 */
const GeneralRow = ({
  label,
  unit,
  unitClass = 'right uom',
  show,
  children,
}: {
  label: React.ReactNode;
  unit?: string;
  unitClass?: string;
  show: unknown;
  children: React.ReactNode;
}) => {
  if (!hbTruthy(show)) return null;
  return (
    <div className="row">
      <div className="col-xs-8 label-part">
        <span data-name="bypass_width_id">{label}</span>
        {unit && <span className={unitClass}>{unit}</span>}
      </div>
      <div className="col-xs-4 output-part">
        <div className="row">
          <div className="col-xs-12 output-part-left no-left-padding">{children}</div>
        </div>
      </div>
    </div>
  );
};

/** the Bypass pane's `col-xs-6` / `col-xs-6` row (tmpl:232-246). */
const BypassRow = ({
  label,
  unit,
  unitClass = 'uom right',
  dataName = 'weight_id',
  valueClass = 'col-xs-12 ',
  children,
}: {
  label: React.ReactNode;
  unit?: string;
  unitClass?: string;
  dataName?: string;
  valueClass?: string;
  children: React.ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-6 label-part">
      <span data-name={dataName}>{label}</span>
      {unit && <span className={unitClass}>{unit}</span>}
    </div>
    <div className="col-xs-6 output-part">
      <div className="row">
        <div className={valueClass}>{children}</div>
      </div>
    </div>
  </div>
);

/** the two-line column heading used above the SUPPLY/EXHAUST pair. */
const PairHeadings = () => (
  <div className="row">
    <div className="col-xs-8 col-xs-offset-4">
      <div className="row">
        <div className="col-xs-5 sub-titles no-right-padding  no-left-padding">
          <span className="text-uppercase sub-titles__text left-padding-5 ">SUPPLY AIR LEAVING</span>
        </div>
        <div className="col-xs-6 col-xs-offset-1 sub-titles no-right-padding">
          <span className="text-uppercase sub-titles__text left-padding-5">EXHAUST AIR LEAVING</span>
        </div>
      </div>
    </div>
  </div>
);

/** the right column's "GENERAL" heading (tmpl:646-654). */
const GeneralHeading = () => (
  <div className="row">
    <div className="col-xs-7">
      <div className="row">
        <div className="col-xs-12 sub-titles no-right-padding ">
          <span className="text-uppercase sub-titles__text ">General</span>
        </div>
      </div>
    </div>
  </div>
);

/** legacy `self.items_count = 9` (js:204) — the pager window size. */
const PHX_PAGER_ITEMS = 9;

/**
 * The candidate pager (legacy `render_phxs` js:3138-3160 → `items_list.tmpl`,
 * plus `showOrHideButtons` :3162-3192 and `prev_next` :3193-3199).
 *
 * Both arrows exist always and start `non-clickable`; they stay `hide` until the
 * list is longer than the 9-chip window, and then scroll it one chip per click.
 * A single candidate still draws its "1" chip.
 */
const PhxPager = ({
  count,
  activeIndex,
  onSelectRow,
  more,
}: {
  count: number;
  activeIndex: number;
  onSelectRow: (i: number) => void;
  /** legacy `rated_phxs_more` = `rated_phxs.length > 10` (js:3005). */
  more: boolean;
}) => {
  const [firstVisible, setFirstVisible] = useState(0);
  const lastVisible = firstVisible + PHX_PAGER_ITEMS;
  const paged = count > PHX_PAGER_ITEMS;
  const arrow = (dir: -1 | 1) => {
    const disabled = dir < 0 ? firstVisible === 0 : lastVisible >= count - 1;
    return (
      <button
        id={dir < 0 ? 'prev_item' : 'next_item'}
        type="button"
        data-step={dir}
        className={`${dir < 0 ? 'icon-angle-left' : 'icon-angle-right'} prev_next_item${
          paged ? '' : ' hide'
        }${disabled ? ' non-clickable' : ''}`}
        onClick={disabled || !paged ? undefined : () => setFirstVisible((f) => f + dir)}
      />
    );
  };
  return (
    <div
      className={`phx-counts phx_items_count col-xs-6 ${count ? '' : 'phx-counts-disable '}${
        more ? 'justify-space-between' : 'gap-5'
      }`}
    >
      {arrow(-1)}
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          data-id={i}
          className={`item item_${i} phx-counts-numbers phx_counts_numbers${
            i === activeIndex ? ' active' : ''
          }${paged && (i < firstVisible || i > lastVisible) ? ' hide' : ''}`}
          role="button"
          onClick={() => onSelectRow(i)}
        >
          {i + 1}
        </div>
      ))}
      {arrow(1)}
    </div>
  );
};

// ---------------------------------------------------------------------------

/** the nav rail, in template order (tmpl:3-19). The pane ids are hardcoded
 *  `ahu-` on dev in BOTH modes — `render_outputs` pins `let prefix = 'ahu-'`
 *  and comments the header-mode branch out (js:2907-2908). */
const OUTPUT_TABS: ReadonlyArray<{ tab: PhxOutputTab; label: string; id: string; nav: string }> = [
  { tab: 'properties', label: 'Heat-Exchanger', id: 'ahu-Properties_output', nav: 'ahu-Properties-output' },
  { tab: 'performance', label: 'Performance', id: 'ahu-Performance_output', nav: 'ahu-Performance-output' },
  { tab: 'bypass', label: 'Bypass', id: 'ahu-Bypass_output', nav: 'ahu-Bypass-output' },
  { tab: 'notes', label: 'Notes', id: 'ahu-Notes-output', nav: 'ahu-Notes-output' },
];

export const PlateHeatExchangerRatedOutputs = ({
  rows,
  ratedIndex,
  onSelectRow,
  fromApi,
  ratedPhx: r,
  currentModel,
  state: st,
  phxData,
  dataForRate,
  bankDeep,
  airStreams,
  shaftShapes,
  shaftDiameters,
  shaftLengths,
  notes,
  errors,
  warnings,
  splitedNotes,
  notFound,
  ratedDatetime,
  version,
  isHeatex,
  season,
  onSeasonChange,
  outputTab,
  onOutputTabChange,
}: PlateHeatExchangerRatedOutputsProps) => {
  const masterPhx = (r.master_phx ?? {}) as PhxRow;
  const configId = Number(st.configuration_id ?? currentModel.configuration_id);
  /** tmpl:11-12 — Bypass is hidden for `Heat-Exchanger` (1) and `Heat-Exchanger
   *  w/o Bypass` (3); the same two values `toggleBypassVisibility` hides the
   *  input Bypass tab for. */
  const bypassHidden = configId === 1 || configId === 3;

  // the season sub-tabs are gated on the RATE INPUT's airflow, not the output
  // (tmpl:405/409 + `checkOutputPerformanceTab` js:3094-3116).
  const winterAirflow = Number(dataForRate.oa_airflow_actual_winter ?? 0);
  const summerAirflow = Number(dataForRate.oa_airflow_actual_summer ?? 0);
  const hasWinter = winterAirflow > 0;
  const hasSummer = summerAirflow > 0;
  // `checkOutputPerformanceTab` forces the season onto whichever pane exists.
  const activeSeason: PhxSeason = !hasWinter && hasSummer ? 'summer' : !hasSummer && hasWinter ? 'winter' : season;

  const warnEntries = Object.entries(splitedNotes ?? {}).filter(([, v]) => hbTruthy(v));
  /** js:3069-3071 — warnings light the Notes anchor; tmpl:16 does the same for
   *  `splitedNotes`. (The template's second arm, `ok == 'false'`, compares a
   *  boolean against the STRING 'false' and can never fire.) */
  const notesWarn = warnEntries.length > 0 || warnings.length > 0;

  const paneClass = (tab: PhxOutputTab, extra = '') =>
    `tab-pane fade scrollable-content col-xs-12 float-left full-height${extra}${
      outputTab === tab ? ' in active' : ''
    }`;

  const streamName = (id: unknown) => raw(byId(airStreams, id)?.name);

  // ---- rows shared by the Heat-Exchanger, Performance and Notes panes ------
  const productCodeRow = (
    <LeftRow label="Bank Product Code" rowClass="product-code-model">
      <OneValue>
        <span data-name="bypass_width_id">{raw(r.product_code)}</span>
      </OneValue>
    </LeftRow>
  );
  const exchangerModelRow = (
    <div className="row product-code-model">
      <div className="col-xs-4 label-part label-part_model_title">
        <span data-name="bypass_width_id">Exchanger Model</span>
      </div>
      <div className="col-xs-8 output-part">
        <div className="row">
          <OneValue>
            {/* the `{{else}}` arm is `selectedModelData.selectedModel` — see the
                file header; it holds an object on dev and prints
                "[object Object]", so it renders empty here. */}
            <span data-name="bypass_width_id"> {raw(r.model)}</span>
          </OneValue>
        </div>
      </div>
    </div>
  );

  // -------------------------------------------------------------------------
  // Performance pane — one season column set (tmpl:417-804 winter / :807-1139
  // summer). The two halves are identical bar the field suffix and two rows
  // (winter has Frosted Surface, summer has Condensation), so they share one
  // renderer keyed on the suffix.
  // -------------------------------------------------------------------------
  const seasonPane = (sea: PhxSeason) => {
    const k = (base: string) => r[`${base}_${sea}`];
    return (
      <div
        id={sea === 'winter' ? 'output_winter' : 'output_summer'}
        className={
          sea === 'winter'
            ? `winter tab-pane fade in${activeSeason === 'winter' ? ' active' : ''}`
            : `summer fade in tab-pane${activeSeason === 'summer' ? ' active' : ''}`
        }
      >
        <div
          className="col-xs-12 col-md-12 col-lg-7 output-part-left"
          style={{ marginTop: '-7px' }}
        >
          {isHeatex && productCodeRow}
          {exchangerModelRow}
        </div>

        <div className="col-xs-12 col-md-12 col-lg-7 output-part-left">
          <PairHeadings />

          <LeftRow label="Air Stream">
            <PairValues
              supply={
                <span data-name="bypass_width_id">{streamName(st[`oa_air_stream_${sea}_id`])}</span>
              }
              exhaust={
                <span data-name="bypass_width_id" className="left-padding-5">
                  {streamName(st[`ra_air_stream_${sea}_id`])}
                </span>
              }
            />
          </LeftRow>

          <LeftRow label="Airflow" unit="CFM">
            <PairValues
              supply={
                <span data-name="bypass_width_id" className="number_output" data-formatter_type="integer">
                  {int(pick(dataForRate[`oa_airflow_actual_${sea}`], k('oa_airflow_actual')))}
                </span>
              }
              exhaust={
                <span
                  data-name="bypass_width_id"
                  className="number_output left-padding-5"
                  data-formatter_type="integer"
                >
                  {int(pick(dataForRate[`ra_airflow_actual_${sea}`], k('ra_airflow_actual')))}
                </span>
              }
            />
          </LeftRow>

          <LeftRow label="DB Temperature" unit="F" unitClass="uom right temp">
            <PairValues
              supply={
                <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">
                  {dec(k('sa_db_temp'))}
                </span>
              }
              exhaust={
                <span
                  data-name="bypass_width_id"
                  className="number_output left-padding-5"
                  data-formatter_type="decimal"
                >
                  {dec(k('ea_db_temp'))}
                </span>
              }
            />
          </LeftRow>

          {/* Heatex reports wet-bulb where Recuperator reports relative humidity
              (tmpl:524-555) — the same `show-for-heatex` split the input pane has. */}
          <LeftRow
            label={isHeatex ? 'WB Temperature' : 'Humidity'}
            unit={isHeatex ? 'F' : '%'}
            unitClass={isHeatex ? 'uom right temp' : 'uom right'}
          >
            <PairValues
              supply={
                <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">
                  {dec(isHeatex ? k('sa_wb_temp') : k('sa_rh'))}
                </span>
              }
              exhaust={
                <span
                  data-name="bypass_width_id"
                  className="number_output left-padding-5"
                  data-formatter_type="decimal"
                >
                  {dec(isHeatex ? k('ea_wb_temp') : k('ea_rh'))}
                </span>
              }
            />
          </LeftRow>

          <LeftRow label="Face Velocity" unit="fpm">
            <PairValues
              supply={
                <span data-name="bypass_width_id" className="number_output" data-formatter_type="integer">
                  {int(k('sa_face_velocity'))}
                </span>
              }
              exhaust={
                <span
                  data-name="bypass_width_id"
                  className="number_output left-padding-5"
                  data-formatter_type="integer"
                >
                  {int(k('ea_face_velocity'))}
                </span>
              }
            />
          </LeftRow>

          {/* Recuperator additionally prints the catalogue's max permissible dP
              after a slash, when the vendor returned one (tmpl:587-590). */}
          <LeftRow label={isHeatex ? 'Air PD' : 'Air PD / Max'} unit="in WC">
            <PairValues
              supply={
                <>
                  <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">
                    {dec(k('sa_apd'))}
                  </span>
                  {!isHeatex && hbTruthy(r.max_permissible_dp) && (
                    <>
                      {' / '}
                      <span
                        data-name="bypass_width_id"
                        className="number_output"
                        data-formatter_type="decimal"
                      >
                        {dec(r.max_permissible_dp)}
                      </span>
                    </>
                  )}
                </>
              }
              exhaust={
                <span
                  data-name="bypass_width_id"
                  className="number_output left-padding-5"
                  data-formatter_type="decimal"
                >
                  {dec(k('ea_apd'))}
                </span>
              }
            />
          </LeftRow>

          {/* winter-only (tmpl:599-617) */}
          {sea === 'winter' &&
            hbTruthy(one(r.sa_frost_surface_percent_winter, r.ea_frost_surface_percent_winter)) && (
              <LeftRow label="Frosted Surface" unit="%">
                <PairValues
                  supply={
                    <span data-name="bypass_width_id">{raw(r.sa_frost_surface_percent_winter)}</span>
                  }
                  exhaust={
                    <span data-name="bypass_width_id" className="left-padding-5">
                      {raw(r.ea_frost_surface_percent_winter)}
                    </span>
                  }
                />
              </LeftRow>
            )}

          {/* summer-only, and SUPPLY only — the template gives it no exhaust
              cell at all (tmpl:993-1007). */}
          {sea === 'summer' && hbTruthy(r.sa_condensation_rate_summer) && (
            <LeftRow label="Condensation" unit="gph">
              <div className="col-xs-5 output-part-left left-padding-5">
                <span data-name="bypass_width_id">{raw(r.sa_condensation_rate_summer)}</span>
              </div>
            </LeftRow>
          )}
        </div>

        <div className="col-xs-12 col-md-12 col-lg-5 output-part-right">
          <GeneralHeading />

          <GeneralRow
            label="Temperature Effectiveness Dry / Wet"
            unit="%"
            show={one(k('dry_temp_effectiveness'), k('wet_temp_effectiveness'))}
          >
            {hbTruthy(k('dry_temp_effectiveness')) && (
              <span className="not-empty" data-name="bypass_width_id" data-formatter_type="decimal">
                {dec(k('dry_temp_effectiveness'))}
              </span>
            )}{' '}
            {hbTruthy(k('wet_temp_effectiveness')) && (
              <span className="not-empty" data-name="bypass_width_id" data-formatter_type="decimal">
                {dec(k('wet_temp_effectiveness'))}
              </span>
            )}
          </GeneralRow>

          <GeneralRow
            label="Temperature Ratio Dry / Wet"
            unit="%"
            show={one(k('dry_temp_ratio'), k('wet_temp_ratio'))}
          >
            {hbTruthy(k('dry_temp_ratio')) && (
              <span data-name="bypass_width_id" data-formatter_type="decimal" className="not-empty">
                {dec(k('dry_temp_ratio'))}
              </span>
            )}{' '}
            {hbTruthy(k('wet_temp_ratio')) && (
              <span
                data-name="bypass_width_id"
                data-formatter_type="decimal"
                className="right-span not-empty"
              >
                {dec(k('wet_temp_ratio'))}
              </span>
            )}
          </GeneralRow>

          {/* winter-only (tmpl:702-718) */}
          {sea === 'winter' && (
            <GeneralRow
              label="Frost Starting Temperature"
              unit="F"
              unitClass="uom temp right"
              show={r.frost_starting_temp_winter}
            >
              <span data-name="bypass_width_id">{raw(r.frost_starting_temp_winter)}</span>
            </GeneralRow>
          )}

          <GeneralRow label="Heat Recovery Class" show={r.hr_class}>
            <span data-name="bypass_width_id">{raw(r.hr_class)}</span>
          </GeneralRow>

          <GeneralRow
            label={
              <>
                Energy Efficiency EN13053
                {!isHeatex && ' / ASHRAE'}
              </>
            }
            unit="%"
            show={one(r.energy_efficiency_en13053, r[`efficiency_${sea}_ashrae`])}
          >
            <span data-name="bypass_width_id">{raw(r.energy_efficiency_en13053)}</span>
            {!isHeatex && (
              <span data-name={`efficiency_${sea}`}>
                {hbTruthy(r[`efficiency_${sea}_ashrae`])
                  ? ` / ${raw(r[`efficiency_${sea}_ashrae`])}`
                  : ''}
              </span>
            )}
          </GeneralRow>

          <GeneralRow label="HRS Capacity" unit="btu/hr" show={k('hrs_capacity')}>
            <span data-name="bypass_width_id">
              <span data-formatter_type="integer">{int(k('hrs_capacity'))}</span>
            </span>
          </GeneralRow>

          <GeneralRow label="ERP 1253/2014" unit="%" show={k('erp_1253_2014')}>
            <span data-name="bypass_width_id">{raw(k('erp_1253_2014'))}</span>
          </GeneralRow>
        </div>
      </div>
    );
  };

  /**
   * A damper-shaft value span. Legacy resolves the DISPLAYED name and the
   * `data-id`/`data-value` attributes from two different sources: the attributes
   * come from the RATED value only, the text falls back to the saved model's id
   * (tmpl:256-274). Select then reads `data-id` straight back off this span
   * (js:1909-1911), so a saved-but-not-rated shaft shows its name while writing
   * back null — a legacy behaviour, preserved.
   */
  const ShaftSpan = ({
    cls,
    dataName,
    list,
    ratedValue,
    savedId,
  }: {
    cls: string;
    dataName: string;
    list: PhxOptionRow[];
    ratedValue: unknown;
    savedId: unknown;
  }) => {
    const rated = byValue(list, ratedValue);
    const shown = hbTruthy(ratedValue) ? rated : byId(list, savedId);
    return (
      <span
        className={cls}
        data-name={dataName}
        data-id={rated ? String(rated.id) : undefined}
        data-value={rated ? String(rated.value) : undefined}
      >
        {raw(shown?.name)}
      </span>
    );
  };

  return (
    <>
      {/* ===== output tab rail (tmpl:1-21) ===== */}
      <div className="col-xs-1 nav nav-tabs">
        <div className="row">
          <ul className="col-xs-12 ouptut_tubs ouptut-menu" style={{ paddingLeft: '15px' }}>
            <li className="cursor-default">Output</li>
            {OUTPUT_TABS.map(({ tab, label, id, nav }) => {
              const isNotes = tab === 'notes';
              const cls = [
                nav,
                'output_label',
                tab === 'bypass' && bypassHidden ? 'hidden' : '',
                outputTab === tab ? 'selected active' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <li key={tab} data-tab={id} className={cls} data-type={isNotes ? 'Notes' : undefined}>
                  {/* bootstrap's tab plugin drives the pane on dev; here the
                      pane is state-driven, so the href is kept only so the
                      anchor stays keyboard-focusable. */}
                  <a
                    className={isNotes && notesWarn ? 'warning-notes' : undefined}
                    data-toggle="tab"
                    href={`#${id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onOutputTabChange(tab);
                    }}
                  >
                    {label}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="col-xs-11 with-border tab-content">
        <div className="row tab-content-row">
          <div className="full-height">
            {notFound ? (
              // tmpl:29 — the message fills the whole output area; the inline
              // inset is legacy's, and is what centres it across the pane
              // instead of leaving it wrapped in a narrow column.
              <div
                className="error-message"
                style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
              >
                {' '}
                Unable to find a Plate Heat Exchanger that satisfies all inputted parameters.
              </div>
            ) : (
              <>
                <PhxPager
                  count={fromApi ? rows.length : 0}
                  activeIndex={ratedIndex}
                  onSelectRow={onSelectRow}
                  more={rows.length > 10}
                />

                {/* ===== Heat-Exchanger (tmpl:32-224) ===== */}
                <div className={paneClass('properties')} id="ahu-Properties_output" name="ahu-Properties_output">
                  <div className="col-xs-12 col-md-12 col-lg-7 output-part-left">
                    {isHeatex && <div className="row empty" />}
                    {isHeatex && productCodeRow}
                    {!isHeatex && <div className="row empty" />}
                    {exchangerModelRow}
                    {isHeatex && (
                      <LeftRow label="Exchanger Qty">
                        <OneValue>
                          <span data-name="bypass_width_id">{raw(r.number_of_modules)}</span>
                        </OneValue>
                      </LeftRow>
                    )}
                  </div>

                  <div className="col-xs-12 col-md-12 col-lg-7 output-part-left">
                    <LeftRow label="Casing W x H x D" unit="in">
                      <OneValue>
                        <span
                          data-name="bypass_width_id"
                          className="number_output"
                          data-formatter_type="decimal"
                        >
                          {dec(r.casing_width)}
                        </span>
                        <span className="unit not-uom "> x </span>
                        <span data-name="bypass_width_id">
                          {raw(pick(r.casing_height, masterPhx.casing_height))}
                        </span>
                        <span className="unit not-uom "> x </span>
                        <span data-name="bypass_width_id">
                          {raw(pick(r.casing_depth, pick(phxData.casing_depth, masterPhx.casing_depth)))}
                        </span>
                      </OneValue>
                    </LeftRow>

                    {!isHeatex && (
                      <LeftRow label="Bank W x H x D">
                        <OneValue>
                          <span data-name="bypass_width_id">
                            {raw(pick(dataForRate.bank_wide, r.bank_wide))}
                          </span>
                          <span className="unit not-uom "> x </span>
                          <span data-name="bypass_width_id">
                            {raw(pick(dataForRate.bank_high, r.bank_high))}
                          </span>{' '}
                          <span className="unit not-uom "> x </span>
                          <span data-name="bypass_width_id">{raw(bankDeep)}</span>
                        </OneValue>
                      </LeftRow>
                    )}

                    {!isHeatex && (
                      <LeftRow label="Module Qty" dataName="airway_width_id">
                        <OneValue>
                          <span data-name="airway_width_id">{raw(r.module_qty)}</span>
                        </OneValue>
                      </LeftRow>
                    )}

                    {!isHeatex && hbTruthy(one(r.airway_width)) && (
                      <LeftRow label="Airway Width" unit="in" dataName="airway_width_id">
                        <OneValue>
                          <span data-name="airway_width_id">{raw(r.airway_width)}</span>
                        </OneValue>
                      </LeftRow>
                    )}

                    <LeftRow label="Plate Spacing" unit="in" dataName="plate_spacing">
                      <OneValue>
                        <span data-name="plate_spacing">
                          {raw(one(r.plate_spacing, phxData.plate_spacing, masterPhx.plate_spacing))}
                        </span>
                      </OneValue>
                    </LeftRow>

                    <LeftRow label="Plate Thickness" unit="in" dataName="plate_thickness">
                      <OneValue>
                        <span data-name="plate_thickness">
                          {raw(one(r.plate_thickness, phxData.plate_thickness, masterPhx.plate_thickness))}
                        </span>
                      </OneValue>
                    </LeftRow>

                    {isHeatex && (
                      <LeftRow label="End Plate Style" dataName="e_plate_style">
                        <OneValue>
                          <span data-name="e_plate_style">{raw(r.e_plate_style)}</span>
                        </OneValue>
                      </LeftRow>
                    )}

                    <LeftRow
                      label="End Plate Flange"
                      dataName="end_plate_flange_id"
                      rowClass="bottom-row"
                    >
                      <OneValue>
                        <span data-name="end_plate_flange">
                          {raw(pick(st.end_plate_flange, currentModel.end_plate_flange))}
                        </span>
                      </OneValue>
                    </LeftRow>

                    <LeftRow
                      label="Bank Weight"
                      unit="lb"
                      unitClass="uom right"
                      dataName="weight_id"
                      rowClass="bottom-row"
                    >
                      <OneValue>
                        <span data-name="airway_width_id">
                          {raw(pick(r.operating_weight, masterPhx.operating_weight))}
                        </span>
                      </OneValue>
                    </LeftRow>
                  </div>
                </div>

                {/* ===== Bypass (tmpl:225-398) ===== */}
                <div
                  className={paneClass('bypass', bypassHidden ? ' hidden' : '')}
                  id="ahu-Bypass_output"
                  name="ahu-Bypass_output"
                >
                  <div className="row empty" />
                  <div className="col-xs-12 col-md-12 col-lg-6 output-part-left">
                    <div className="row">
                      <div className="col-xs-9 first_column_input">
                        <BypassRow label="Width" unit="in">
                          <span data-name="airway_width_id" data-formatter_type="decimal">
                            {dec(r.bypass_width)}
                          </span>
                        </BypassRow>

                        {/* the shaft rows are for `Heat-Exchanger w/Bypass` (2)
                            only (tmpl:247). */}
                        {configId === 2 && (
                          <>
                            <BypassRow label="Damper Shaft Shape" dataName="dmr_shaft_shape_id">
                              <ShaftSpan
                                cls="shaft_shape"
                                dataName="dmr_shaft_shape_id"
                                list={shaftShapes}
                                ratedValue={r.dmr_shaft_shape}
                                savedId={currentModel.dmr_shaft_shape_id}
                              />
                            </BypassRow>
                            <BypassRow label="Damper Shaft Diameter" unit="in">
                              <ShaftSpan
                                cls="shaft_diameter"
                                dataName="dmr_shaft_dia_id"
                                list={shaftDiameters}
                                ratedValue={r.dmr_shaft_dia}
                                savedId={currentModel.dmr_shaft_dia_id}
                              />
                            </BypassRow>
                            <BypassRow label="Damper Shaft Length" unit="in">
                              <ShaftSpan
                                cls="shaft_length"
                                dataName="dmr_shaft_length_id"
                                list={shaftLengths}
                                ratedValue={r.dmr_shaft_length}
                                savedId={currentModel.dmr_shaft_length_id}
                              />
                            </BypassRow>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-xs-12 col-md-12 col-lg-6 output-part-right">
                    <BypassRow
                      label="Supply Mixed Air DB"
                      unit="F"
                      unitClass="uom temp right"
                      dataName="bypass_width_id"
                      valueClass="col-xs-12 no-left-padding"
                    >
                      <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">
                        {dec(r.sma_db_temp_winter)}
                      </span>
                    </BypassRow>
                    <BypassRow
                      label="Airflow"
                      unit="CFM"
                      dataName="bypass_width_id"
                      valueClass="col-xs-12 no-left-padding"
                    >
                      <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">
                        {dec(r.bypass_airflow)}
                      </span>
                    </BypassRow>
                    <BypassRow
                      label="Face Velocity"
                      unit="fpm"
                      dataName="bypass_width_id"
                      valueClass="col-xs-12 no-left-padding"
                    >
                      <span data-name="bypass_width_id" className="number_output" data-formatter_type="integer">
                        {int(r.bypass_velocity)}
                      </span>
                    </BypassRow>
                    <BypassRow
                      label="Air PD"
                      unit="in WC"
                      dataName="bypass_width_id"
                      valueClass="col-xs-12 no-left-padding"
                    >
                      {/* tmpl:392 misspells the attribute (`data-formater_type`)
                          so dev never formats this one — raw, deliberately. */}
                      <span data-name="bypass_width_id" data-formater_type="decimal">
                        {raw(r.bypass_apd)}
                      </span>
                    </BypassRow>
                  </div>
                </div>

                {/* ===== Performance (tmpl:399-1142) ===== */}
                <div
                  className={paneClass('performance', ' Performance')}
                  id="ahu-Performance_output"
                  name="ahu-Performance_output"
                >
                  <div className="col-xs-12 sub-tab-menu nav-tabs">
                    <div className="row">
                      <ul className="col-xs-12 text-right output_performance_tabing summer-winter-tabing">
                        {(['winter', 'summer'] as const).map((sea) => {
                          const enabled = sea === 'winter' ? hasWinter : hasSummer;
                          return (
                            <li
                              key={sea}
                              data-tab={sea}
                              className={`${sea} ${sea}-tab ${activeSeason === sea ? 'active' : ''} ${
                                enabled ? '' : 'non-clickable'
                              }`}
                            >
                              <a
                                tabIndex={-1}
                                data-toggle="tab"
                                data-tab={sea}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (enabled) onSeasonChange(sea);
                                }}
                              >
                                {sea === 'winter' ? 'winter ' : 'summer'}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                  <div className="output_performance-tab-content">
                    {hasWinter && seasonPane('winter')}
                    {hasSummer && seasonPane('summer')}
                  </div>
                </div>

                {/* ===== Notes (tmpl:1144-1227) ===== */}
                <div
                  className={paneClass('notes', ' output-height')}
                  id="ahu-Notes-output"
                  name="ahu-Notes-output"
                >
                  <div className="col-xs-12 col-md-12 full-height">
                    <div className="row">
                      <div className="col-xs-12">
                        <div className="clearfix row nots_and_errors">
                          <div className="col-xs-12 col-md-12 col-lg-7 output-part-left">
                            {isHeatex && productCodeRow}
                            {exchangerModelRow}
                          </div>

                          {errors.length ? (
                            <div className="col-xs-12 ">
                              <div className="row">
                                <div className="col-xs-2">
                                  <p className="warning-notes">Error :</p>
                                </div>
                                <div className="col-xs-10">
                                  {errors.map((e, i) => (
                                    <p className="warning-msg" key={i}>
                                      {e}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              {warnEntries.length > 0 && (
                                <div className="col-xs-12">
                                  <div className="row">
                                    <div className="col-xs-2">
                                      <p className="warning-notes">Warning : </p>
                                    </div>
                                    <div className="col-xs-10">
                                      {warnEntries.map(([kk, v]) => (
                                        <p className="warning-notes label" key={kk}>
                                          {` ${kk} : ${raw(v)}`}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {notes.length > 0 && (
                                <div className="col-xs-12 col-md-12 col-lg-7 output-part-left">
                                  <div className="row">
                                    <div className="col-xs-4">
                                      <p className="label">Mfg&apos;s Notes : </p>
                                    </div>
                                    <div className="col-xs-8 left-padding-5">
                                      {notes.map((n, i) => (
                                        <p className=" label" key={i}>
                                          {raw(n.note)}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* the vendor DLL release, revealed only while the Notes tab is open
            (tmpl:1233-1239 + `activateLabel` js:1765-1767). Recuperator only. */}
        {!isHeatex && (
          <div
            className={`output-bottom output-part ${
              outputTab === 'notes' ? '' : 'hide '
            }only-for-notes`}
          >
            <span data-name="airway_width_id">DLL release</span>
            <span data-name="airway_width_id">
              {raw(pick(version, masterPhx.api_dll_version))}
            </span>
          </div>
        )}
      </div>

      {/* tmpl:1241-1247 — offset by the rail's column so it sits under the sheet. */}
      <div className="col-xs-11 col-xs-offset-1">
        <div className="row">
          <div className="rating-time" id="rating_time">
            Rated on : {raw(pick(ratedDatetime, currentModel.rating_datetime))}
          </div>
        </div>
      </div>
    </>
  );
};

export default PlateHeatExchangerRatedOutputs;
